import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import EditableText from './EditableText';
import { useEnquiry } from '../context/EnquiryContext';

const SIDDHA_LOGO =
  'https://customer-assets.emergentagent.com/job_ebb063e1-3e83-418f-bb38-602ef1351305/artifacts/yspj6tyg_Union%20of%20Shiva%20and%20Parvati.png';

const EnquiryModal = ({ onClose }) => {
  const { openEnquiry } = useEnquiry();
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const cards = [
    {
      key: 'live',
      headingId: 'enquiry.card.live.heading',
      headingDefault: 'Join Live Classes',
      bodyId: 'enquiry.card.live.body',
      bodyDefault: 'Live online classes and energy exchange with Sanjay Ji, advancing you in the journey of self discovery through mastering Siddha kriyas (beginner friendly)',
      testid: 'enquiry-card-live',
    },
    {
      key: 'healing',
      headingId: 'enquiry.card.healing.heading',
      headingDefault: 'Book a Siddha Healing Session',
      bodyId: 'enquiry.card.healing.body',
      bodyDefault: 'One-on-one session · Personalised Siddha Kriya guidance and energetic healing with Sanjay Ji',
      testid: 'enquiry-card-healing',
    },
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4"
      data-testid="enquiry-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onMouseDown={onClose} data-testid="enquiry-modal-overlay" />
      <div
        className="relative w-full max-w-2xl card-mystical rounded-2xl border border-primary/40 p-6 sm:p-8 shadow-2xl"
        style={{ background: 'rgba(20,8,2,0.97)' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-primary/60 hover:text-primary transition-colors"
          aria-label="Close"
          data-testid="enquiry-modal-close"
        >
          <X size={20} />
        </button>

        <div className="flex flex-col items-center text-center mb-6">
          <img
            src={SIDDHA_LOGO}
            alt="Siddha Samaj"
            width="52"
            height="52"
            style={{
              borderRadius: '50%',
              border: '1.5px solid rgba(212,168,67,0.6)',
              objectFit: 'cover',
              objectPosition: 'center 30%',
              backgroundColor: '#0a0a0a',
              boxShadow: '0 0 16px rgba(212,168,67,0.3)',
            }}
          />
          <h2 className="mt-3 text-2xl font-heading golden-text text-glow tracking-wide">
            <EditableText id="enquiry.modal.heading" defaultValue="Begin the Siddha Path" />
          </h2>
          <p className="text-sm text-primary/70 mt-1 italic">
            <EditableText
              id="enquiry.modal.subheading"
              defaultValue="Live practice · Siddha healing · Direct guidance from Sanjay Ji"
            />
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {cards.map((c) => (
            <div
              key={c.key}
              className="rounded-xl border border-primary/30 bg-primary/[0.04] p-5 flex flex-col"
              data-testid={c.testid}
            >
              <h3 className="text-lg font-heading text-primary tracking-wide mb-2">
                <EditableText id={c.headingId} defaultValue={c.headingDefault} />
              </h3>
              <p className="text-sm text-primary/75 leading-relaxed flex-1 mb-5">
                <EditableText id={c.bodyId} defaultValue={c.bodyDefault} multiline />
              </p>
              <button
                type="button"
                onClick={() => { onClose(); setTimeout(() => openEnquiry(c.key), 10); }}
                className="ornate-button px-5 py-2.5 text-sm font-heading text-primary uppercase tracking-wider rounded-lg text-center"
                data-testid={`${c.testid}-cta`}
              >
                <EditableText id="enquiry.card.cta" defaultValue="Send Enquiry" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

const AnnouncementBar = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div
        className="fixed top-0 left-0 right-0 z-[55] notranslate"
        style={{ background: '#1e0e04', height: 32, borderBottom: '1px solid rgba(212,168,67,0.18)' }}
        data-testid="announcement-bar"
      >
        <div className="h-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between gap-3">
          <div
            className="min-w-0 truncate"
            style={{ color: '#d4a843', fontSize: 11, letterSpacing: '0.05em' }}
            data-testid="announcement-text"
          >
            <EditableText
              id="announcement.text"
              defaultValue="The Siddha path is open. Live practice. Siddha healing. Begin today."
            />
          </div>

          {/* Desktop: two pills */}
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="announce-pill rounded-full px-3 py-[3px]"
              style={{ fontSize: 10, letterSpacing: '0.04em' }}
              data-testid="announce-join-live"
            >
              <EditableText id="announcement.pill.live" defaultValue="Join Live Classes" />
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="announce-pill rounded-full px-3 py-[3px]"
              style={{ fontSize: 10, letterSpacing: '0.04em' }}
              data-testid="announce-book-healing"
            >
              <EditableText id="announcement.pill.healing" defaultValue="Book a Healing Session" />
            </button>
          </div>

          {/* Mobile: single Begin button */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="sm:hidden announce-pill rounded-full px-3 py-[3px] shrink-0"
            style={{ fontSize: 10, letterSpacing: '0.04em' }}
            data-testid="announce-begin"
          >
            <EditableText id="announcement.pill.mobile" defaultValue="Begin →" />
          </button>
        </div>
      </div>

      {open && <EnquiryModal onClose={() => setOpen(false)} />}
    </>
  );
};

export default AnnouncementBar;
